import { CommandCenterSecurityRight } from '../../../security/modules/command.center.security.module';
import { FinanceSecurityRight } from '../../../security/modules/finance.security.module';
import { ActivityEntityName } from '../../shared/activity.entity.name';
import { EntityQueue, EntityQueueType } from './entity.queue';

// Invoices and bills are Entity queues only: nothing raises flags on them yet, so a Flag queue would stay empty
const QUEUE_TARGETS: Record<EntityQueueType, ReadonlySet<ActivityEntityName>> = {
  [EntityQueueType.Flag]: new Set([ActivityEntityName.Job, ActivityEntityName.WorkOrder, ActivityEntityName.JobStages]),
  [EntityQueueType.Entity]: new Set([ActivityEntityName.Job, ActivityEntityName.WorkOrder, ActivityEntityName.JobStages, ActivityEntityName.Invoice, ActivityEntityName.Bill]),
};

// Queue rights are Command Center rights only, so a finance queue also needs the right that guards the documents themselves
const TARGET_RIGHTS: Partial<Record<ActivityEntityName, FinanceSecurityRight>> = {
  [ActivityEntityName.Invoice]: FinanceSecurityRight.CanAccessInvoices,
  [ActivityEntityName.Bill]: FinanceSecurityRight.CanAccessBills,
};

// Jobs mark their number as the identifier in metadata; financial documents do not, so a queue names theirs
const FINANCE_IDENTIFIER_PATHS: Partial<Record<ActivityEntityName, string>> = {
  [ActivityEntityName.Invoice]: 'invoiceNumber',
  [ActivityEntityName.Bill]: 'invoiceNumber',
};

/** The field that identifies a record in a queue on `entity` when its metadata flags none, or undefined. */
export function entityQueueIdentifierPath(entity: ActivityEntityName | undefined): string | undefined {

  return entity ? FINANCE_IDENTIFIER_PATHS[entity] : undefined;
}

/** The entity a queue lists: a Flag queue's flags sit on `targetEntity`, an Entity queue lists `entity`. */
export function entityQueueTarget(queue: Partial<Pick<EntityQueue, 'type' | 'entity' | 'targetEntity'>> | null | undefined): ActivityEntityName | undefined {

  return queue?.type === EntityQueueType.Flag ? queue.targetEntity : queue?.entity;
}

export function isAllowedEntityQueueTarget(type: EntityQueueType | undefined, entity: ActivityEntityName | undefined): boolean {

  return !!type && !!entity && !!QUEUE_TARGETS[type]?.has(entity);
}

/** The right a user needs, beyond queue rights, to see the records a queue on `entity` lists; undefined when none. */
export function entityQueueTargetRight(entity: ActivityEntityName | undefined): FinanceSecurityRight | undefined {

  return entity ? TARGET_RIGHTS[entity] : undefined;
}

/** Whether a user holding `rights` may see the records a queue on `entity` lists. */
export function canAccessEntityQueueTarget(entity: ActivityEntityName | undefined, rights: readonly number[] | null | undefined): boolean {

  const required = entityQueueTargetRight(entity);
  return required === undefined || !!rights?.includes(required);
}

/** The rights someone needs to use a queue on `entity`: seeing queues at all, plus the finance right for invoice and bill queues. */
export function entityQueueRecipientRights(entity: ActivityEntityName | undefined): number[] {

  const targetRight = entityQueueTargetRight(entity);
  return targetRight === undefined ? [CommandCenterSecurityRight.CanViewQueues] : [CommandCenterSecurityRight.CanViewQueues, targetRight];
}
